import { TournamentCard } from '../../../components/common/cards';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useRoleContext } from '../../../context/RoleContext';
import { fetchTournaments } from '../../../services/tournamentService';
import { getOrganizationId, getPlayerManagerDashboardPath } from '../../../utils/organization';
import { getClubId, getPlayerManagerDashboardPath as getClubPlayerManagerDashboardPath } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';
import {
  getCommunityId,
  getPlayerManagerDashboardPath as getCommunityPlayerManagerDashboardPath,
} from '../../../utils/community';
import {
  buildManagementScope,
  buildPlayerManagerTournamentPath,
  getKnownTournamentId,
} from '../../../utils/managementNavigation';

const normalizeId = (value) => {
  if (!value) return '';

  return value.toString ? value.toString() : `${value}`;
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const PlayerManagerTournamentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    organizationId: routeOrganizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    playerManagerId: routePlayerManagerId,
  } = useParams();
  const { token, user } = useAuth();
  const { activeContext } = useRoleContext();
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const userRoles = useMemo(
    () => new Set([...(user?.roles || []), user?.role].filter(Boolean)),
    [user]
  );
  const isOrganizationAdmin = userRoles.has('organization_admin');
  const isClubAdmin = userRoles.has('club_admin');
  const isTournamentAdmin = userRoles.has('tournament_admin');
  const isTournamentManager = userRoles.has('tournament_manager');
  const isSuperAdmin = userRoles.has('super_admin');
  const isTeamAdmin = userRoles.has('team_admin');

  const normalizedUserId = useMemo(() => normalizeId(user?.id || user?._id), [user?.id, user?._id]);
  const managerScopeId = useMemo(
    () => normalizeId(routePlayerManagerId) || normalizedUserId,
    [normalizedUserId, routePlayerManagerId]
  );
  const organizationScope = routeOrganizationId || user?.organization;
  const organizationId = useMemo(() => getOrganizationId(organizationScope), [organizationScope]);
  const communityScope = routeCommunityId || user?.community;
  const communityId = useMemo(() => getCommunityId(communityScope), [communityScope]);
  const clubScope = routeClubId || user?.club;
  const clubId = useMemo(() => getClubId(clubScope), [clubScope]);
  const canView =
    isSuperAdmin ||
    isOrganizationAdmin ||
    isClubAdmin ||
    isTournamentAdmin ||
    isTournamentManager ||
    (isTeamAdmin && managerScopeId === normalizedUserId);
  const knownTournamentId = useMemo(
    () =>
      getKnownTournamentId({
        params: {},
        location,
      }),
    [location],
  );
  const managementScope = useMemo(
    () =>
      buildManagementScope({
        organizationId,
        communityId,
        clubId,
        entityContext: activeContext,
      }),
    [activeContext, clubId, communityId, organizationId],
  );

  useEffect(() => {
    if (!knownTournamentId || !managerScopeId) return;

    const redirectPath = buildPlayerManagerTournamentPath(
      managementScope,
      managerScopeId,
      knownTournamentId,
    );

    if (redirectPath) {
      navigate(redirectPath, { replace: true, state: { tournamentId: knownTournamentId } });
    }
  }, [knownTournamentId, managementScope, managerScopeId, navigate]);

  useEffect(() => {
    const loadTournaments = async () => {
      if (!token || !canView) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchTournaments(token, organizationScope, { communityId, clubId });
        setTournaments(response.tournaments || []);
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [canView, clubId, communityId, organizationScope, token]);

  const assignedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments) || !managerScopeId) return [];

    return tournaments.filter((tournament) => {
      const registrationManagerId = normalizeId(
        tournament?.registrationManager?.id || tournament?.registrationManager?._id
      );

      return registrationManagerId && registrationManagerId === managerScopeId;
    });
  }, [managerScopeId, tournaments]);

  const handleTournamentClick = (tournamentId) => {
    if (!managerScopeId || !tournamentId) return;

    const nextPath = buildPlayerManagerTournamentPath(managementScope, managerScopeId, tournamentId);

    if (nextPath) {
      navigate(nextPath, { state: { tournamentId } });
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const playerManagerPath =
    (communityId
      ? getCommunityPlayerManagerDashboardPath(communityId, managerScopeId)
      : clubId
        ? getClubPlayerManagerDashboardPath(clubId, managerScopeId)
        : getPlayerManagerDashboardPath(organizationId, managerScopeId)) ||
    (communityId && managerScopeId
      ? `/community/${communityId}/player-manager/${managerScopeId}/tournaments`
      : clubId && managerScopeId
        ? `/club/${clubId}/player-manager/${managerScopeId}/tournaments`
        : organizationId && managerScopeId
          ? `/org/${organizationId}/player-manager/${managerScopeId}/tournaments`
          : '/');

  return (
    <div className="py-4 d-grid gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Player manager</p>
          <h1 className="h4 mb-2">Assigned tournaments</h1>
          <p className="text-muted mb-0">
            Open a tournament to manage player registrations and rosters.
          </p>
        </div>
        <div>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/management')}>
            Back to Manager workspace
          </button>
        </div>
      </div>

      {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
      {isLoading && <div className="alert alert-secondary mb-0">Loading tournaments...</div>}

      {!isLoading && assignedTournaments.length === 0 && (
        <div className="alert alert-info mb-0">No tournaments are assigned to you yet.</div>
      )}

      {!isLoading &&
        assignedTournaments.map((tournament) => {
          const tournamentId =
            tournament.id ||
            tournament._id?.toString?.() ||
            tournament.tournamentId ||
            tournament._id;

          return (
    <PageShell containerClassName="p-0">
            <TournamentCard
              key={tournamentId}
              tournament={tournament}
              onClick={() => handleTournamentClick(tournamentId)}
              onManage={() => handleTournamentClick(tournamentId)}
              showActions
            />
          
    </PageShell>
  );
        })}

      {!isLoading && assignedTournaments.length > 0 && (
        <div className="text-muted small">
          Access link: <span className="text-nowrap">{playerManagerPath}</span>
        </div>
      )}
    </div>
  );
};

export default PlayerManagerTournamentsPage;
